/**
 * UICanvas Component (W-UI)
 *
 * Mounts a compiled `.uix` screen as a screen-space overlay (v1 — world-space canvases are a later
 * epic). Data-only like Text/SpriteRenderer: the UI subsystem (editor/player) watches these,
 * loads the compiled UI module for `root`, creates the Vue app over the retained UI tree, and the
 * shared UIPass draws it at RenderStage.UI. See .refactor/UI-ENGINE-DESIGN.md.
 */

import {
  allowMultiple,
  category,
  color,
  component,
  displayName,
  executeInEditor,
  field,
  icon,
} from "../attributes";

/** How logical UI units map to physical pixels (design §Rendering — the scaler owns hi-DPI). */
export enum UIScaleMode {
  /** Layout in a fixed logical height (referenceHeight); scale = viewportHeight / referenceHeight. */
  ScaleWithHeight = 0,
  /** 1 logical unit = 1 physical pixel. */
  ConstantPixel = 1,
}

@component("core.UICanvas")
@displayName("UI Canvas")
@icon("LayoutTemplate")
@color("#38bdf8")
@category("UI")
@executeInEditor(true)
@allowMultiple(false)
export class UICanvas {
  @field({ type: "asset", assetType: "uix", label: "Root Screen", tooltip: "The .uix screen this canvas mounts" })
  root: string = "";

  @field({
    type: "i32",
    label: "Scale Mode",
    widget: "dropdown",
    group: "Scaling",
    options: [
      { label: "Scale With Height", value: UIScaleMode.ScaleWithHeight },
      { label: "Constant Pixel", value: UIScaleMode.ConstantPixel },
    ],
  })
  scaleMode: number = UIScaleMode.ScaleWithHeight;

  @field({ type: "f32", label: "Reference Height", min: 1, group: "Scaling", tooltip: "Logical viewport height for Scale With Height" })
  referenceHeight: number = 1080;

  @field({ type: "bool", label: "Pixel Perfect", group: "Scaling", tooltip: "Snap rect edges to physical pixels" })
  pixelPerfect: boolean = true;

  @field({ type: "bool", label: "Antialias", group: "Scaling", tooltip: "Smooth rect/border edges (off = hard pixel-art edges); text is always antialiased" })
  antialias: boolean = true;

  @field({ type: "i32", label: "Sort Order", tooltip: "Canvases draw in ascending order" })
  sortOrder: number = 0;

  @field({ type: "bool", label: "Visible" })
  visible: boolean = true;

  // ── Runtime API (cold, like Text) ────────────────────────────────────────

  /** Swap the mounted screen (empty unmounts). */
  setRoot(assetId: string): void {
    this.root = assetId;
  }

  /** Toggle canvas visibility. */
  setVisible(visible: boolean): void {
    this.visible = visible;
  }
}
